import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * The one location selection, shared by the dashboard and every report
 * (CHG-2026-09-manager-reporting, D16).
 *
 * Five pages each implement their own restaurant picker today — two in local component state, one
 * hiding the control entirely when the tenant has a single restaurant, two in their own
 * feature-scoped stores. Selecting a location on one page and navigating to another loses it, which
 * is the inconsistency the owner named. This is the replacement for the new surfaces; the existing
 * five are deliberately left alone rather than growing a sixth variant.
 *
 * An **empty list means all locations** — the absence of a restaurant filter rather than a list of
 * every id. The distinction is not pedantry: a store opened next month is in the sum on the day it
 * opens, where a stored list of today's ids would leave it out until somebody noticed. Picking
 * every location therefore collapses back to the empty list (CHG-2026-09-article-location-filter).
 *
 * This is the first store under `src/shared/store/`, which is where a genuinely app-wide store
 * belongs, unlike the two feature-scoped restaurant stores it does not touch.
 */
interface LocationState {
  restaurantIds: Array<number>
  setRestaurantIds: (ids: Array<number>) => void
}

export const useLocationStore = create<LocationState>()(
  persist(
    (set) => ({
      restaurantIds: [],
      setRestaurantIds: (ids) => set({ restaurantIds: ids }),
    }),
    {
      name: 'report-location',
      // v1 held a single `restaurantId`. Without this, every manager who had picked a store would
      // silently rehydrate into a state with no `restaurantIds` at all and the reports would throw
      // on the first `.length`.
      version: 2,
      migrate: (persisted, version) => {
        if (version >= 2) return persisted as LocationState
        const legacy = persisted as { restaurantId?: number | null } | null
        const id = legacy?.restaurantId
        return {
          restaurantIds: typeof id === 'number' ? [id] : [],
        } as LocationState
      },
    },
  ),
)
